/** Billing (/billing): plan & credits, credit packs paid by card (Polar) or VietQR bank transfer (SePay), and credit history */

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertCircle, CheckCircle2, FlaskConical, Info } from 'lucide-react'
import { useT } from '../i18n'
import { cn } from '../lib/utils'
import { cardClass, errorTextClass, hintClass } from '../components/ui/styles'
import { useCreditBalance, useCreditPacks, useCreateCheckout, usePaymentsConfig, type CreditPack } from '../hooks/use-credits'
import { BillingBanner } from '../components/billing/billing-banner'
import { CreditBalanceCard } from '../components/billing/credit-balance-card'
import { CreditPackCard } from '../components/billing/credit-pack-card'
import { PurchaseHistory, TransactionHistory } from '../components/billing/credit-history'
import { SepayTransferDialog } from '../components/billing/sepay-transfer-dialog'

const POPULAR_PACK_ID = 'pack_starter'
/** Status params from the Polar redirect are cleared after this delay */
const STATUS_PARAM_TTL_MS = 5000

const sectionHeadingClass = 'font-display text-lg font-semibold tracking-tight text-fg'

export function BillingPage() {
  const t = useT()
  const [searchParams, setSearchParams] = useSearchParams()

  const balanceQuery = useCreditBalance()
  const { data: packs, isLoading: packsLoading, error: packsError } = useCreditPacks()
  const { data: paymentsConfig } = usePaymentsConfig()
  const checkout = useCreateCheckout()
  const [missingCheckoutUrl, setMissingCheckoutUrl] = useState(false)
  const [transferPack, setTransferPack] = useState<CreditPack | null>(null)
  const refetchBalance = balanceQuery.refetch

  const status = searchParams.get('status')
  // Polar appends `customer_session_token` to its redirect; in some flows it
  // replaces our `?status=success` query entirely, so treat it as success too.
  const isSuccess = status === 'success' || searchParams.has('customer_session_token')
  const isCancel = status === 'cancel'
  const isSandbox = paymentsConfig?.environment === 'sandbox'
  const sepayEnabled = paymentsConfig?.methods?.sepay === true

  // After the Polar redirect: refresh the balance (success only) and clear the params
  useEffect(() => {
    if (!isSuccess && !isCancel) return
    if (isSuccess) void refetchBalance()
    const timer = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('status')
          next.delete('customer_session_token')
          return next
        },
        { replace: true },
      )
    }, STATUS_PARAM_TTL_MS)
    return () => clearTimeout(timer)
  }, [isSuccess, isCancel, refetchBalance, setSearchParams])

  const handleBuy = async (packId: string) => {
    setMissingCheckoutUrl(false)
    try {
      const result = await checkout.mutateAsync({ packId })
      if (!result?.checkoutUrl) {
        setMissingCheckoutUrl(true)
        return
      }
      window.location.href = result.checkoutUrl
    } catch {
      // Shown through checkout.error below
    }
  }

  const isCheckoutUnconfigured = checkout.error?.status === 503
  const isPacksUnconfigured = packsError?.status === 503
  const checkoutErrorMessage = checkout.error && !isCheckoutUnconfigured ? checkout.error.message : null
  const pendingPackId = checkout.isPending ? checkout.variables?.packId : undefined

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-6 md:px-8 md:py-8">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-fg">{t('billing.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('billing.subtitle')}</p>
      </header>

      {(isSandbox || isSuccess || isCancel || isCheckoutUnconfigured || isPacksUnconfigured || checkoutErrorMessage || missingCheckoutUrl) && (
        <div className="space-y-3">
          {isSandbox && (
            <BillingBanner tone="accent" icon={FlaskConical} title={t('billing.banner.sandboxTitle')} body={t('billing.banner.sandboxBody')} />
          )}
          {isSuccess && (
            <BillingBanner tone="success" icon={CheckCircle2} title={t('billing.banner.successTitle')} body={t('billing.banner.successBody')} />
          )}
          {isCancel && <BillingBanner tone="neutral" icon={Info} title={t('billing.banner.cancelTitle')} body={t('billing.banner.cancelBody')} />}
          {(isCheckoutUnconfigured || isPacksUnconfigured) && (
            <BillingBanner
              tone="warning"
              icon={AlertCircle}
              title={t('billing.banner.unconfiguredTitle')}
              body={t('billing.banner.unconfiguredBody')}
            />
          )}
          {checkoutErrorMessage && (
            <BillingBanner tone="danger" icon={AlertCircle} title={t('billing.banner.checkoutFailed')} body={checkoutErrorMessage} />
          )}
          {missingCheckoutUrl && <BillingBanner tone="danger" icon={AlertCircle} title={t('billing.banner.noCheckoutUrl')} />}
        </div>
      )}

      <section aria-labelledby="billing-overview" className="space-y-3">
        <h2 id="billing-overview" className={sectionHeadingClass}>
          {t('billing.overview.heading')}
        </h2>
        <CreditBalanceCard
          balance={balanceQuery.data}
          isLoading={balanceQuery.isLoading}
          isError={balanceQuery.isError}
          onRetry={() => void refetchBalance()}
        />
      </section>

      <section aria-labelledby="billing-buy" className="space-y-3">
        <h2 id="billing-buy" className={sectionHeadingClass}>
          {t('billing.buy.heading')}
        </h2>

        {packsError && !isPacksUnconfigured && (
          <p className={errorTextClass} role="alert">
            {t('billing.buy.packsError')}
          </p>
        )}

        {packsLoading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-busy="true">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={cn(cardClass, 'h-56 animate-pulse bg-surface-muted')} />
            ))}
          </div>
        )}

        {packs && packs.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {packs.map((pack) => (
              <CreditPackCard
                key={pack.id}
                pack={pack}
                isPopular={pack.id === POPULAR_PACK_ID}
                disabled={checkout.isPending}
                isRedirecting={pendingPackId === pack.id}
                onBuy={(id) => void handleBuy(id)}
                onBankTransfer={sepayEnabled ? setTransferPack : undefined}
              />
            ))}
          </div>
        )}

        {packs && packs.length === 0 && <p className="text-sm text-fg-subtle">{t('billing.buy.noPacks')}</p>}

        <p className={hintClass}>{sepayEnabled ? t('billing.buy.footerWithTransfer') : t('billing.buy.footer')}</p>
        {transferPack && <SepayTransferDialog pack={transferPack} onClose={() => setTransferPack(null)} />}
      </section>

      <section aria-labelledby="billing-history" className="space-y-3">
        <h2 id="billing-history" className={sectionHeadingClass}>
          {t('billing.history.heading')}
        </h2>
        <TransactionHistory />
        <PurchaseHistory />
      </section>
    </div>
  )
}
